"use client"
import { SearchBar } from '@/components/ui';
import { useNotifications } from '@/contexts/NotificationContext';
import * as Checkbox from '@radix-ui/react-checkbox';
import * as Select from '@radix-ui/react-select';
import * as Separator from '@radix-ui/react-separator';
import {
  AlertCircle,
  Bell,
  Calendar,
  Check,
  CheckCircle,
  ChevronDown,
  Clock,
  Eye,
  FileText,
  Filter,
  Settings,
  Tag,
  User,
  X
} from 'lucide-react';
import { useEffect, useState } from 'react';

export default function Notifications() {
  const {
    notifications: allNotifications,
    markAsRead: contextMarkAsRead,
    markAllAsRead: contextMarkAllAsRead,
    deleteNotification: contextDeleteNotification,
    fetchNotifications,
  } = useNotifications();

  const [filterType, setFilterType] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [showUnreadOnly, setShowUnreadOnly] = useState<boolean>(false);
  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true,
    pushNotifications: true,
    blogSchedulingAlerts: true,
    blogPublishingAlerts: true,
    systemNotifications: true,
    userActivityAlerts: false
  });

  // Fetch all notifications on mount
  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const markAsRead = async (id: string) => {
    await contextMarkAsRead(id);
  };

  const markAllAsRead = async () => {
    await contextMarkAllAsRead();
  };

  const deleteNotification = async (id: string) => {
    await contextDeleteNotification(id);
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'blog-scheduled':
        return <Clock className="h-5 w-5 text-blue-600" />;
      case 'blog-published':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'blog-draft':
        return <FileText className="h-5 w-5 text-orange-600" />;
      case 'system':
        return <AlertCircle className="h-5 w-5 text-purple-600" />;
      case 'user-activity':
        return <User className="h-5 w-5 text-gray-600" />;
      default:
        return <Bell className="h-5 w-5 text-gray-600" />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'border-l-red-500 bg-red-50';
      case 'medium':
        return 'border-l-yellow-500 bg-yellow-50';
      case 'low':
        return 'border-l-green-500 bg-green-50';
      default:
        return 'border-l-gray-500 bg-gray-50';
    }
  };

  const getPriorityLabel = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'High Priority';
      case 'medium':
        return 'Medium Priority';
      case 'low':
        return 'Low Priority';
      default:
        return 'Unknown Priority';
    }
  };

  const formatTimestamp = (timestamp: string | Date) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));

    if (diffInHours < 1) {
      return 'Just now';
    } else if (diffInHours < 24) {
      return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  const filteredNotifications = allNotifications.filter(notification => {
    const matchesType = filterType === 'all' || notification.type === filterType;
    const matchesSearch = notification.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      notification.message.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesReadStatus = !showUnreadOnly || !notification.isRead;

    return matchesType && matchesSearch && matchesReadStatus;
  });

  const unreadCount = allNotifications.filter(n => !n.isRead).length;

  return (
    <div className="space-y-6">
      {/* Header with Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Bell className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Total Notifications</p>
              <p className="text-2xl font-bold text-gray-900">{allNotifications.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <AlertCircle className="h-6 w-6 text-red-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Unread</p>
              <p className="text-2xl font-bold text-gray-900">{unreadCount}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Clock className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Scheduled</p>
              <p className="text-2xl font-bold text-gray-900">
                {allNotifications.filter(n => n.type === 'blog-scheduled').length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <FileText className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Published</p>
              <p className="text-2xl font-bold text-gray-900">
                {allNotifications.filter(n => n.type === 'blog-published').length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Actions */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
            <div className="flex items-center space-x-2">
              <Filter className="h-4 w-4 text-gray-500" />
              <Select.Root value={filterType} onValueChange={setFilterType}>
                <Select.Trigger className="flex items-center justify-between px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none  focus:border-transparent">
                  <Select.Value />
                  <Select.Icon>
                    <ChevronDown className="h-4 w-4" />
                  </Select.Icon>
                </Select.Trigger>
                <Select.Portal>
                  <Select.Content className="bg-white border border-gray-200 rounded-md shadow-lg">
                    <Select.Viewport className="p-1">
                      <Select.Item
                        value="all"
                        className="relative flex items-center px-8 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded cursor-pointer"
                      >
                        <Select.ItemText>All Types</Select.ItemText>
                        <Select.ItemIndicator className="absolute left-2">
                          <Check className="h-4 w-4" />
                        </Select.ItemIndicator>
                      </Select.Item>
                      <Select.Item
                        value="blog-scheduled"
                        className="relative flex items-center px-8 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded cursor-pointer"
                      >
                        <Select.ItemText>Blog Scheduled</Select.ItemText>
                        <Select.ItemIndicator className="absolute left-2">
                          <Check className="h-4 w-4" />
                        </Select.ItemIndicator>
                      </Select.Item>
                      <Select.Item
                        value="blog-published"
                        className="relative flex items-center px-8 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded cursor-pointer"
                      >
                        <Select.ItemText>Blog Published</Select.ItemText>
                        <Select.ItemIndicator className="absolute left-2">
                          <Check className="h-4 w-4" />
                        </Select.ItemIndicator>
                      </Select.Item>
                      <Select.Item
                        value="blog-draft"
                        className="relative flex items-center px-8 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded cursor-pointer"
                      >
                        <Select.ItemText>Blog Draft</Select.ItemText>
                        <Select.ItemIndicator className="absolute left-2">
                          <Check className="h-4 w-4" />
                        </Select.ItemIndicator>
                      </Select.Item>
                      <Select.Item
                        value="system"
                        className="relative flex items-center px-8 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded cursor-pointer"
                      >
                        <Select.ItemText>System</Select.ItemText>
                        <Select.ItemIndicator className="absolute left-2">
                          <Check className="h-4 w-4" />
                        </Select.ItemIndicator>
                      </Select.Item>
                      <Select.Item
                        value="user-activity"
                        className="relative flex items-center px-8 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded cursor-pointer"
                      >
                        <Select.ItemText>User Activity</Select.ItemText>
                        <Select.ItemIndicator className="absolute left-2">
                          <Check className="h-4 w-4" />
                        </Select.ItemIndicator>
                      </Select.Item>
                    </Select.Viewport>
                  </Select.Content>
                </Select.Portal>
              </Select.Root>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox.Root
                id="unread-only"
                checked={showUnreadOnly}
                // ... existing code ...
                onCheckedChange={(checked) => setShowUnreadOnly(checked === true)}
                // ... existing code ...
                className="h-4 w-4 rounded border border-gray-300 bg-white data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600 focus:outline-none  focus:ring-offset-2"
              >
                <Checkbox.Indicator>
                  <Check className="h-3 w-3 text-white" />
                </Checkbox.Indicator>
              </Checkbox.Root>
              <label htmlFor="unread-only" className="text-sm text-gray-700">
                Unread only
              </label>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <SearchBar
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Search notifications..."
              className="w-64"
            />

            <button
              onClick={markAllAsRead}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium"
            >
              Mark All Read
            </button>
          </div>
        </div>
      </div>

      {/* Notifications List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            Notifications ({filteredNotifications.length})
          </h3>
        </div>

        <div className="divide-y divide-gray-200">
          {filteredNotifications.length === 0 ? (
            <div className="px-6 py-12 text-center">
              <Bell className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No notifications found</h3>
              <p className="text-gray-500">Try adjusting your filters or search criteria.</p>
            </div>
          ) : (
            filteredNotifications.map((notification) => (
              <div
                key={notification._id}
                className={`p-6 border-l-4 ${getPriorityColor(notification.priority)} hover:bg-gray-50 transition-colors ${!notification.isRead ? 'bg-blue-50' : ''
                  }`}
              >
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 mt-1">
                    {getNotificationIcon(notification.type)}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <h4 className={`text-sm font-medium ${!notification.isRead ? 'text-blue-900' : 'text-gray-900'}`}>
                            {notification.title}
                          </h4>
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${notification.priority === 'high' ? 'bg-red-100 text-red-800' :
                            notification.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-green-100 text-green-800'
                            }`}>
                            {getPriorityLabel(notification.priority)}
                          </span>
                          <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded-full">
                            {notification.category}
                          </span>
                        </div>

                        <p className="text-sm text-gray-600 mb-3">
                          {notification.message}
                        </p>

                        {notification.metadata && (
                          <div className="flex flex-wrap items-center space-x-4 text-xs text-gray-500">
                            {notification.metadata.author && (
                              <div className="flex items-center space-x-1">
                                <User className="h-3 w-3" />
                                <span>{notification.metadata.author}</span>
                              </div>
                            )}
                            {notification.metadata.scheduledDate && (
                              <div className="flex items-center space-x-1">
                                <Calendar className="h-3 w-3" />
                                <span>Scheduled: {new Date(notification.metadata.scheduledDate).toLocaleDateString()}</span>
                              </div>
                            )}
                            {notification.metadata.publishDate && (
                              <div className="flex items-center space-x-1">
                                <CheckCircle className="h-3 w-3" />
                                <span>Published: {new Date(notification.metadata.publishDate).toLocaleDateString()}</span>
                              </div>
                            )}
                            {notification.metadata.tags && notification.metadata.tags.length > 0 && (
                              <div className="flex items-center space-x-1">
                                <Tag className="h-3 w-3" />
                                <span>{notification.metadata.tags.join(', ')}</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      <div className="flex items-center space-x-2 ml-4">
                        <span className="text-xs text-gray-500">
                          {formatTimestamp(notification.timestamp)}
                        </span>

                        {!notification.isRead && (
                          <button
                            onClick={() => markAsRead(notification._id!)}
                            className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                            title="Mark as read"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                        )}

                        <button
                          onClick={() => deleteNotification(notification._id!)}
                          className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                          title="Delete notification"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Notification Settings */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center space-x-3 mb-6">
          <Settings className="h-6 w-6 text-gray-600" />
          <h2 className="text-xl font-semibold text-gray-900">Notification Settings</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Email Notifications</h3>
            <div className="space-y-3">
              <label className="flex items-center space-x-3">
                <Checkbox.Root
                  checked={notificationSettings.emailNotifications}
                  onCheckedChange={(checked) => setNotificationSettings(prev => ({
                    ...prev,
                    emailNotifications: checked as boolean
                  }))}
                  className="h-4 w-4 rounded border border-gray-300 bg-white data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600 focus:outline-none  focus:ring-offset-2"
                >
                  <Checkbox.Indicator>
                    <Check className="h-3 w-3 text-white" />
                  </Checkbox.Indicator>
                </Checkbox.Root>
                <span className="text-sm text-gray-700">Enable email notifications</span>
              </label>

              <label className="flex items-center space-x-3">
                <Checkbox.Root
                  checked={notificationSettings.blogSchedulingAlerts}
                  onCheckedChange={(checked) => setNotificationSettings(prev => ({
                    ...prev,
                    blogSchedulingAlerts: checked as boolean
                  }))}
                  className="h-4 w-4 rounded border border-gray-300 bg-white data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600 focus:outline-none  focus:ring-offset-2"
                >
                  <Checkbox.Indicator>
                    <Check className="h-3 w-3 text-white" />
                  </Checkbox.Indicator>
                </Checkbox.Root>
                <span className="text-sm text-gray-700">Blog scheduling alerts</span>
              </label>

              <label className="flex items-center space-x-3">
                <Checkbox.Root
                  checked={notificationSettings.blogPublishingAlerts}
                  onCheckedChange={(checked) => setNotificationSettings(prev => ({
                    ...prev,
                    blogPublishingAlerts: checked as boolean
                  }))}
                  className="h-4 w-4 rounded border border-gray-300 bg-white data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600 focus:outline-none  focus:ring-offset-2"
                >
                  <Checkbox.Indicator>
                    <Check className="h-3 w-3 text-white" />
                  </Checkbox.Indicator>
                </Checkbox.Root>
                <span className="text-sm text-gray-700">Blog publishing alerts</span>
              </label>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Push Notifications</h3>
            <div className="space-y-3">
              <label className="flex items-center space-x-3">
                <Checkbox.Root
                  checked={notificationSettings.pushNotifications}
                  onCheckedChange={(checked) => setNotificationSettings(prev => ({
                    ...prev,
                    pushNotifications: checked as boolean
                  }))}
                  className="h-4 w-4 rounded border border-gray-300 bg-white data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600 focus:outline-none  focus:ring-offset-2"
                >
                  <Checkbox.Indicator>
                    <Check className="h-3 w-3 text-white" />
                  </Checkbox.Indicator>
                </Checkbox.Root>
                <span className="text-sm text-gray-700">Enable push notifications</span>
              </label>

              <label className="flex items-center space-x-3">
                <Checkbox.Root
                  checked={notificationSettings.systemNotifications}
                  onCheckedChange={(checked) => setNotificationSettings(prev => ({
                    ...prev,
                    systemNotifications: checked as boolean
                  }))}
                  className="h-4 w-4 rounded border border-gray-300 bg-white data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600 focus:outline-none  focus:ring-offset-2"
                >
                  <Checkbox.Indicator>
                    <Check className="h-3 w-3 text-white" />
                  </Checkbox.Indicator>
                </Checkbox.Root>
                <span className="text-sm text-gray-700">System notifications</span>
              </label>

              <label className="flex items-center space-x-3">
                <Checkbox.Root
                  checked={notificationSettings.userActivityAlerts}
                  onCheckedChange={(checked) => setNotificationSettings(prev => ({
                    ...prev,
                    userActivityAlerts: checked as boolean
                  }))}
                  className="h-4 w-4 rounded border border-gray-300 bg-white data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600 focus:outline-none  focus:ring-offset-2"
                >
                  <Checkbox.Indicator>
                    <Check className="h-3 w-3 text-white" />
                  </Checkbox.Indicator>
                </Checkbox.Root>
                <span className="text-sm text-gray-700">User activity alerts</span>
              </label>
            </div>
          </div>
        </div>

        <Separator.Root className="h-px bg-gray-200 my-6" />
        <div className="pt-6">
          <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium">
            Save Settings
          </button>
        </div>
      </div>
    </div>
  );
}
